import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import type { CSWState } from '@/lib/services/whatsapp/types';
import { latestInboundAt, resolveCswView } from '@/lib/services/whatsapp/cswLive';
import { onWindowClosed } from '@/lib/services/whatsapp/cloudProxyError';
import { e164FromAny } from '@/lib/services/whatsapp/identity';

/** Oltre questo tempo senza risposta la lettura della finestra si considera non riuscita. */
const CSW_READ_TIMEOUT_MS = 8000;

/**
 * Hook per lo stato della Customer Service Window 24h (CSW) di una chat Cloud.
 *
 * - Per provider WAHA → `isCloud=false`, `cswState=null` (WAHA non ha vincolo finestra).
 * - Per provider Cloud → legge `provider.getCSWState(chatId)` (da `whatsapp_csw_state`),
 *   che indica se la finestra è aperta e quanti minuti restano.
 * - Quando arriva un messaggio del cliente nella chat aperta, la finestra si
 *   riapre subito (cswWithInbound) e lo stato viene riletto dal DB poco dopo.
 *
 * Al cambio chat lo stato si azzera: finche' la lettura non arriva e' "sconosciuto"
 * (`cswUnknown`) e il testo libero resta bloccato. Si ricalcola ogni minuto.
 * Se Meta respinge un invio per finestra chiusa (onWindowClosed) la finestra
 * risulta chiusa finche' il cliente non riscrive.
 *
 * `canSendFreeForm`: false quando la finestra è chiusa o sconosciuta. Vale per TUTTO quello che
 * non è un modello approvato: testo, allegati, vocali, posizione, contatto.
 * Il composer usa questa proprietà per bloccare l'invio e proporre i modelli.
 */
export function useCloudCsw(chatId: string | null) {
  const provider = useWhatsAppChatStore((s) => s.provider);
  const messages = useWhatsAppChatStore((s) => s.messages);
  // Lettura legata alla chat: al cambio chat quella vecchia non vale piu' (stato "sconosciuto").
  const [db, setDb] = useState<{ chatId: string; state: CSWState | null } | null>(null);
  // Invio respinto da Meta per finestra chiusa in questa chat.
  const [forced, setForced] = useState<{ chatId: string; at: number } | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const chatRef = useRef(chatId);
  chatRef.current = chatId;

  const isCloud = !!provider && provider.type !== 'whatsapp' && typeof provider.getCSWState === 'function';

  const refresh = useCallback(async () => {
    if (!isCloud || !chatId || !provider?.getCSWState) {
      setDb(null);
      return;
    }
    const forChat = chatId;
    let next: CSWState | null = null;
    try {
      // Tetto di attesa: se la lettura non arriva mai, l'invio non resta bloccato per sempre
      // (stato null = non si blocca, Meta rispondera' con l'errore giusto).
      next = await Promise.race([
        provider.getCSWState(forChat),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), CSW_READ_TIMEOUT_MS)),
      ]);
    } catch {
      next = null;
    }
    // Risposta arrivata per una chat non piu' aperta: si ignora.
    if (chatRef.current !== forChat) return;
    setDb({ chatId: forChat, state: next });
    setNowMs(Date.now());
  }, [isCloud, chatId, provider]);

  // A ogni cambio chat si azzera tutto e si rilegge.
  useEffect(() => {
    setDb(null);
    setForced(null);
    setNowMs(Date.now());
    refresh();
  }, [refresh]);

  // Ricalcolo ogni minuto: la finestra che scade a chat aperta viene vista.
  useEffect(() => {
    if (!isCloud || !chatId) return;
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [isCloud, chatId]);

  // Meta ha respinto un invio perche' la finestra e' chiusa: si propongono i modelli.
  useEffect(() => onWindowClosed((cid) => {
    const current = chatRef.current;
    if (!current || (cid !== current && e164FromAny(cid) !== e164FromAny(current))) return;
    setForced({ chatId: current, at: Date.now() });
    setNowMs(Date.now());
  }), []);

  // Ultimo messaggio del cliente nella chat aperta: quando cambia, la finestra
  // si aggiorna subito e si rilegge il DB (il webhook lo aggiorna subito dopo).
  const lastInbound = useMemo(
    () => (isCloud ? latestInboundAt(messages, chatId) : null),
    [isCloud, messages, chatId],
  );
  useEffect(() => {
    if (lastInbound === null) return;
    const t = setTimeout(() => { refresh(); }, 2000);
    return () => clearTimeout(t);
  }, [lastInbound, refresh]);

  const view = useMemo(
    () => resolveCswView({
      isCloud,
      chatId,
      loadedFor: db?.chatId ?? null,
      dbState: db?.chatId === chatId ? db.state : null,
      lastInbound,
      forcedClosedAt: forced && forced.chatId === chatId ? forced.at : null,
      nowMs,
    }),
    [isCloud, chatId, db, lastInbound, forced, nowMs],
  );

  return {
    isCloud,
    cswState: view.cswState,
    /** True finche' la lettura della finestra per questa chat non e' arrivata. */
    cswUnknown: view.unknown,
    refresh,
    canSendFreeForm: view.canSendFreeForm,
  };
}
